/**
 * myDayUtils.js — pure helpers for My Day (owner's personal daily planner).
 *
 * No React, no Firestore in here: every function is deterministic and
 * unit-tested from myDayUtils.test.js. Dates are LOCAL 'YYYY-MM-DD' strings
 * so a day never shifts under the owner because of a UTC boundary.
 */

// ── Dates ──────────────────────────────────────────────────────────────────

/** Local calendar date as 'YYYY-MM-DD'. */
export function todayStr(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** 'YYYY-MM-DD' → local Date at midnight. */
export function parseDay(s) {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function addDays(s, n) {
  const d = parseDay(s);
  d.setDate(d.getDate() + n);
  return todayStr(d);
}

/** Whole days from `b` to `a` (positive when `a` is later). */
export function diffDays(a, b) {
  return Math.round((parseDay(a).getTime() - parseDay(b).getTime()) / 86400000);
}

export function dayOfWeek(s) { return parseDay(s).getDay(); }

/** Monday-start week containing `s`. */
export function startOfWeek(s) {
  const dow = dayOfWeek(s);
  return addDays(s, dow === 0 ? -6 : 1 - dow);
}

export function fmtDay(s, opts = { weekday: 'short', month: 'short', day: 'numeric' }) {
  return parseDay(s).toLocaleDateString(undefined, opts);
}

/** 'Today' / 'Tomorrow' / 'Yesterday' / 'Mon, Sep 28'. */
export function relativeDay(s, today = todayStr()) {
  const d = diffDays(s, today);
  if (d === 0) return 'Today';
  if (d === 1) return 'Tomorrow';
  if (d === -1) return 'Yesterday';
  return fmtDay(s);
}

/** 95 → '1h 35m'. */
export function fmtDuration(min) {
  const m = Math.max(0, Math.round(Number(min) || 0));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h}h ${rest}m` : `${h}h`;
}

/** '14:30' → '2:30 PM'. */
export function fmtTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = String(hhmm).split(':').map(Number);
  if (Number.isNaN(h)) return '';
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m || 0).padStart(2, '0')} ${ampm}`;
}

/** Seconds → 'MM:SS'. */
export function fmtClock(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

// ── Constants ──────────────────────────────────────────────────────────────

export const PRIORITIES = [
  { value: 1, label: 'P1 · Critical', short: 'P1', color: '#ef4444' },
  { value: 2, label: 'P2 · High',     short: 'P2', color: '#f59e0b' },
  { value: 3, label: 'P3 · Normal',   short: 'P3', color: '#3b82f6' },
  { value: 4, label: 'P4 · Someday',  short: 'P4', color: '#64748b' },
];

export const priorityMeta = p =>
  PRIORITIES.find(x => x.value === Number(p)) || PRIORITIES[2];

/**
 * Buckets tuned for a food-truck owner's day.
 *
 * Colours are the validated categorical slots for a dark surface, kept in the
 * order that clears the colour-vision separation gates (checked with the
 * data-viz palette validator against #0d1b2a). Every chip also carries its icon
 * and name, so identity is never colour-alone.
 */
export const DEFAULT_CATEGORIES = [
  { id: 'kitchen',   name: 'Kitchen',   icon: '🍳', color: '#3987e5' },
  { id: 'orders',    name: 'Orders',    icon: '🎫', color: '#d95926' },
  { id: 'inventory', name: 'Inventory', icon: '📦', color: '#199e70' },
  { id: 'staff',     name: 'Staff',     icon: '👥', color: '#c98500' },
  { id: 'finance',   name: 'Finance',   icon: '💵', color: '#d55181' },
  { id: 'marketing', name: 'Marketing', icon: '📣', color: '#008300' },
  { id: 'catering',  name: 'Catering',  icon: '🎉', color: '#9085e9' },
  { id: 'personal',  name: 'Personal',  icon: '🏠', color: '#e66767' },
];

export const categoryMeta = (id, cats = DEFAULT_CATEGORIES) =>
  cats.find(c => c.id === id) || { id: id || 'general', name: id || 'General', icon: '•', color: '#64748b' };

export const DEFAULT_SETTINGS = {
  capacityMin: 360,     // realistic focus capacity for a planned day
  autoCarry: true,      // unfinished work rolls to today by itself
  pomodoroMin: 25,
  breakMin: 5,
  startHour: 7,
  endHour: 21,
  remindersOn: true,    // nudge before a time-blocked activity starts
  remindLeadMin: 10,
  staleAfterCarries: 3, // "carried this many times" counts as stale
  finishByHour: 19,     // the hour the planning capacity runs out
};

export const uid = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

// ── Natural-language quick add ─────────────────────────────────────────────

const WEEKDAYS = {
  sun: 0, sunday: 0, mon: 1, monday: 1, tue: 2, tues: 2, tuesday: 2,
  wed: 3, weds: 3, wednesday: 3, thu: 4, thur: 4, thurs: 4, thursday: 4,
  fri: 5, friday: 5, sat: 6, saturday: 6,
};
const MONTHS = {
  jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3,
  may: 4, jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7,
  sep: 8, sept: 8, september: 8, oct: 9, october: 9, nov: 10, november: 10,
  dec: 11, december: 11,
};
const DOW_RE = Object.keys(WEEKDAYS).sort((a, b) => b.length - a.length).join('|');
const MON_RE = Object.keys(MONTHS).sort((a, b) => b.length - a.length).join('|');

/** Next date on/after `from` that falls on weekday `dow`. `skipToday` forces +7. */
function nextWeekday(from, dow, skipToday = false) {
  const cur = dayOfWeek(from);
  let delta = (dow - cur + 7) % 7;
  if (delta === 0 && skipToday) delta = 7;
  return addDays(from, delta);
}

function minutesFrom(amount, unitRaw) {
  const unit = (unitRaw || 'm').toLowerCase();
  const n = Number(amount);
  if (unit.startsWith('h')) return Math.round(n * 60);
  return Math.round(n);
}

/**
 * Parse a Todoist-style quick-add string.
 *
 *   "prep chutney tomorrow 7am p1 @kitchen #prep ~45m every weekday"
 *
 * Understands: p1-p4 / !1-!4, #tags, @category, ~30m ~1.5h estimates,
 * today | tonight | tomorrow | yesterday | mon..sun | next <day|week|month> |
 * in N days/weeks | 9/25 | sep 25 | 25 sep, times (7am, 19:30, at 3),
 * and "every day | weekday | week | month | <day> | N days" recurrence.
 *
 * Returns the draft plus `matched` — the chips the UI echoes back so the
 * owner can see what was understood before pressing Enter.
 */
export function parseQuickAdd(raw, today = todayStr()) {
  let text = ` ${String(raw || '')} `;
  const matched = [];
  const out = {
    title: '', date: null, startTime: null, priority: 3,
    estimateMin: null, tags: [], category: null, recurrence: null,
  };
  const eat = (re, fn) => {
    const m = text.match(re);
    if (!m) return false;
    text = text.replace(m[0], ' ');
    fn(m);
    return true;
  };

  // ~45m / ~1.5h estimate
  eat(/\s~\s?(\d+(?:\.\d+)?)\s*(hours|hour|hrs|hr|h|minutes|minute|mins|min|m)?(?=\s)/i, m => {
    out.estimateMin = minutesFrom(m[1], m[2]);
    matched.push({ kind: 'estimate', text: fmtDuration(out.estimateMin) });
  });

  // p1..p4 / !1..!4
  eat(/\s(?:p|!)([1-4])(?=\s)/i, m => {
    out.priority = Number(m[1]);
    matched.push({ kind: 'priority', text: priorityMeta(out.priority).short });
  });

  // @category (first wins)
  eat(/\s@([\w-]+)(?=\s)/, m => {
    out.category = m[1].toLowerCase();
    matched.push({ kind: 'category', text: `@${out.category}` });
  });

  // #tags (all)
  let tagMatch;
  // eslint-disable-next-line no-cond-assign
  while ((tagMatch = text.match(/\s#([\w-]+)(?=\s)/))) {
    const tag = tagMatch[1].toLowerCase();
    text = text.replace(tagMatch[0], ' ');
    if (!out.tags.includes(tag)) {
      out.tags.push(tag);
      matched.push({ kind: 'tag', text: `#${tag}` });
    }
  }

  // every day | every weekday | every week | every month | every <dow> | every N days
  eat(new RegExp(`\\severy\\s+(?:(\\d+)\\s*days?|(day|daily)|(weekdays?)|(weeks?)|(months?)|(${DOW_RE}))(?=\\s)`, 'i'), m => {
    if (m[1]) out.recurrence = { type: 'everyN', n: Math.max(1, Number(m[1])), startDate: today };
    else if (m[2]) out.recurrence = { type: 'daily' };
    else if (m[3]) out.recurrence = { type: 'weekdays' };
    else if (m[4]) out.recurrence = { type: 'weekly', days: [dayOfWeek(today)] };
    else if (m[5]) out.recurrence = { type: 'monthly', dayOfMonth: parseDay(today).getDate() };
    else if (m[6]) out.recurrence = { type: 'weekly', days: [WEEKDAYS[m[6].toLowerCase()]] };
    matched.push({ kind: 'repeat', text: describeRule(out.recurrence) });
  });

  // Dates — longest / most specific phrasing first
  const setDate = (d, label) => { out.date = d; matched.push({ kind: 'date', text: label || relativeDay(d, today) }); };
  const dateFound =
    eat(new RegExp(`\\snext\\s+(week|month|${DOW_RE})(?=\\s)`, 'i'), m => {
      const w = m[1].toLowerCase();
      if (w === 'week') setDate(addDays(startOfWeek(today), 7));
      else if (w === 'month') { const d = parseDay(today); d.setMonth(d.getMonth() + 1); setDate(todayStr(d)); }
      else setDate(nextWeekday(today, WEEKDAYS[w], true));
    }) ||
    eat(/\s(today|tonight|tod)(?=\s)/i, () => setDate(today)) ||
    eat(/\s(tomorrow|tmrw|tmr)(?=\s)/i, () => setDate(addDays(today, 1))) ||
    eat(/\syesterday(?=\s)/i, () => setDate(addDays(today, -1))) ||
    eat(/\sin\s+(\d+)\s+(days?|weeks?)(?=\s)/i, m =>
      setDate(addDays(today, Number(m[1]) * (/^w/i.test(m[2]) ? 7 : 1)))) ||
    eat(new RegExp(`\\s(?:this\\s+)?(${MON_RE})\\s+(\\d{1,2})(?:st|nd|rd|th)?(?=\\s)`, 'i'), m =>
      setDate(monthDay(MONTHS[m[1].toLowerCase()], Number(m[2]), today))) ||
    eat(new RegExp(`\\s(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MON_RE})(?=\\s)`, 'i'), m =>
      setDate(monthDay(MONTHS[m[2].toLowerCase()], Number(m[1]), today))) ||
    eat(/\s(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?(?=\s)/, m => {
      const year = m[3] ? (m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])) : null;
      setDate(year
        ? todayStr(new Date(year, Number(m[1]) - 1, Number(m[2])))
        : monthDay(Number(m[1]) - 1, Number(m[2]), today));
    }) ||
    eat(new RegExp(`\\s(?:this\\s+)?(${DOW_RE})(?=\\s)`, 'i'), m =>
      setDate(nextWeekday(today, WEEKDAYS[m[1].toLowerCase()])));

  // Times — 7am / 7:30pm / 19:30 / at 3
  eat(/\s(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)(?=\s)/i, m => {
    let h = Number(m[1]) % 12;
    if (/pm/i.test(m[3])) h += 12;
    out.startTime = `${String(h).padStart(2, '0')}:${String(Number(m[2] || 0)).padStart(2, '0')}`;
    matched.push({ kind: 'time', text: fmtTime(out.startTime) });
  }) ||
  eat(/\s(?:at\s+)?([01]?\d|2[0-3]):([0-5]\d)(?=\s)/, m => {
    out.startTime = `${String(Number(m[1])).padStart(2, '0')}:${m[2]}`;
    matched.push({ kind: 'time', text: fmtTime(out.startTime) });
  }) ||
  eat(/\sat\s+(\d{1,2})(?=\s)/i, m => {
    const h = Number(m[1]);
    out.startTime = `${String(h < 7 ? h + 12 : h).padStart(2, '0')}:00`; // "at 3" on a work day means 3pm
    matched.push({ kind: 'time', text: fmtTime(out.startTime) });
  });

  if (!dateFound && out.recurrence) out.date = today;
  out.title = text.replace(/\s+/g, ' ').trim();
  out.matched = matched;
  return out;
}

/** Month/day in the closest sensible year (rolls to next year if long past). */
function monthDay(monthIdx, day, today) {
  const base = parseDay(today);
  let candidate = todayStr(new Date(base.getFullYear(), monthIdx, day));
  if (diffDays(candidate, today) < -180) {
    candidate = todayStr(new Date(base.getFullYear() + 1, monthIdx, day));
  }
  return candidate;
}

// ── Recurrence ─────────────────────────────────────────────────────────────

const DOW_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Does a repeat rule fire on `dateStr`? */
export function occursOn(rule, dateStr) {
  if (!rule) return false;
  if (rule.startDate && diffDays(dateStr, rule.startDate) < 0) return false;
  if (rule.endDate && diffDays(dateStr, rule.endDate) > 0) return false;
  const dow = dayOfWeek(dateStr);
  switch (rule.type) {
    case 'daily':    return true;
    case 'weekdays': return dow >= 1 && dow <= 5;
    case 'weekly':   return (rule.days || []).includes(dow);
    case 'monthly':  return parseDay(dateStr).getDate() === Number(rule.dayOfMonth || 1);
    case 'everyN': {
      const n = Math.max(1, Number(rule.n) || 1);
      return diffDays(dateStr, rule.startDate || dateStr) % n === 0;
    }
    default: return false;
  }
}

export function describeRule(rule) {
  if (!rule) return 'Once';
  switch (rule.type) {
    case 'daily':    return 'Every day';
    case 'weekdays': return 'Every weekday';
    case 'weekly':   return `Every ${(rule.days || []).map(d => DOW_LABEL[d]).join(', ') || 'week'}`;
    case 'monthly':  return `Monthly on the ${ordinal(Number(rule.dayOfMonth || 1))}`;
    case 'everyN':   return `Every ${rule.n} days`;
    default: return 'Once';
  }
}

export function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Materialise routine tasks for `date`, skipping any already generated.
 * Returns only the NEW tasks so the caller can append them.
 */
export function generateRoutineTasks(routines, tasks, date) {
  const existing = new Set(
    tasks.filter(t => t.date === date && t.routineId).map(t => t.routineId)
  );
  return (routines || [])
    .filter(r => r.active !== false && !existing.has(r.id) && occursOn(r.rule, date))
    .map((r, i) => makeTask({
      title: r.title,
      notes: r.notes || '',
      date,
      priority: r.priority ?? 3,
      category: r.category || null,
      tags: r.tags || [],
      estimateMin: r.estimateMin ?? null,
      startTime: r.startTime || null,
      subtasks: (r.subtasks || []).map(s => ({ id: uid(), title: s.title ?? s, done: false })),
      routineId: r.id,
      order: -1000 + i,
    }));
}

// ── Tasks ──────────────────────────────────────────────────────────────────

export function makeTask(patch = {}) {
  const date = patch.date || todayStr();
  return {
    id: uid(),
    title: '',
    notes: '',
    date,
    createdDate: date,
    originalDate: date,
    done: false,
    doneAt: null,
    priority: 3,
    important: false,
    category: null,
    tags: [],
    estimateMin: null,
    startTime: null,
    subtasks: [],
    routineId: null,
    carryCount: 0,
    focusSec: 0,
    pinned: false,
    order: 0,
    archived: false,
    createdAt: Date.now(),
    ...patch,
  };
}

/**
 * Carry unfinished work forward — the heart of the page.
 *
 * Anything still open on a day before `today` moves onto today and keeps a
 * running `carryCount` so a task that has been dodged for a week says so.
 * Tasks the owner parked (`noCarry`) stay put, and nothing scheduled in the
 * future is touched.
 */
export function rollForward(tasks, today = todayStr()) {
  const moved = [];
  const movedByDate = {};
  const next = (tasks || []).map(t => {
    if (t.done || t.archived || t.noCarry) return t;
    if (diffDays(t.date, today) >= 0) return t;
    moved.push(t.id);
    movedByDate[t.date] = (movedByDate[t.date] || 0) + 1;
    return {
      ...t,
      date: today,
      originalDate: t.originalDate || t.date,
      carryCount: (t.carryCount || 0) + 1,
      lastCarriedAt: Date.now(),
    };
  });
  return { tasks: next, moved, movedByDate };
}

/** Days a task has been open past its first planned date. */
export function ageInDays(task, today = todayStr()) {
  return Math.max(0, diffDays(today, task.originalDate || task.createdDate || task.date));
}

/** Eisenhower quadrant: urgency from date + priority, importance from the flag. */
export function quadrantOf(task, today = todayStr()) {
  const urgent = task.priority <= 2 || diffDays(task.date, today) <= 0;
  const important = !!task.important || task.priority === 1;
  if (urgent && important) return 'do';
  if (!urgent && important) return 'schedule';
  if (urgent && !important) return 'delegate';
  return 'drop';
}

export const QUADRANTS = [
  { id: 'do',       title: 'Do first',  hint: 'Urgent + important',      color: '#ef4444' },
  { id: 'schedule', title: 'Schedule',  hint: 'Important, not urgent',   color: '#3b82f6' },
  { id: 'delegate', title: 'Delegate',  hint: 'Urgent, not important',   color: '#f59e0b' },
  { id: 'drop',     title: 'Drop',      hint: 'Neither — cut it',        color: '#64748b' },
];

/** Time-blocked first (by clock), then pinned, priority, manual order. */
export function sortTasks(list) {
  return [...(list || [])].sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
    if (!!a.startTime !== !!b.startTime) return a.startTime ? -1 : 1;
    if (a.startTime && b.startTime && a.startTime !== b.startTime) {
      return a.startTime < b.startTime ? -1 : 1;
    }
    if (a.priority !== b.priority) return (a.priority || 3) - (b.priority || 3);
    if ((a.order || 0) !== (b.order || 0)) return (a.order || 0) - (b.order || 0);
    return (a.createdAt || 0) - (b.createdAt || 0);
  });
}

export function matchesSearch(task, q) {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return [task.title, task.notes, task.category, ...(task.tags || []),
    ...(task.subtasks || []).map(s => s.title)]
    .filter(Boolean).join(' ').toLowerCase().includes(needle);
}

/** Planned vs. capacity for one day — powers the "load" meter. */
export function dayLoad(dayTasks, capacityMin = DEFAULT_SETTINGS.capacityMin) {
  const open = dayTasks.filter(t => !t.done && !t.archived);
  const plannedMin = open.reduce((s, t) => s + (Number(t.estimateMin) || 0), 0);
  const pct = capacityMin > 0 ? Math.round((plannedMin / capacityMin) * 100) : 0;
  return {
    plannedMin,
    capacityMin,
    pct,
    state: pct > 110 ? 'over' : pct > 85 ? 'tight' : 'ok',
  };
}

// ── Reminders ──────────────────────────────────────────────────────────────

/** '14:30' on `date` → millis, or null when the task isn't time-blocked. */
export function taskStartMs(task) {
  if (!task?.startTime || !task?.date) return null;
  const [h, m] = String(task.startTime).split(':').map(Number);
  if (Number.isNaN(h)) return null;
  const d = parseDay(task.date);
  d.setHours(h, m || 0, 0, 0);
  return d.getTime();
}

/**
 * Which time-blocked activities are due a nudge right now.
 *
 * Fires inside a window that opens `leadMin` before the start time and closes
 * at the start time itself, so a tab opened mid-afternoon doesn't dump every
 * reminder the morning already passed. `alreadyFired` holds the keys this
 * device has shown — reminders are per device, not stored on the task, because
 * two screens open in the truck should both nudge.
 */
export function dueReminders(tasks, now = Date.now(), leadMin = 10, alreadyFired = new Set()) {
  const lead = Math.max(0, Number(leadMin) || 0) * 60000;
  return (tasks || []).filter(t => {
    if (t.done || t.archived || !t.startTime) return false;
    if (alreadyFired.has(reminderKey(t))) return false;
    const start = taskStartMs(t);
    if (start == null) return false;
    return start - now <= lead && start - now > -60000; // up to a minute late still counts
  });
}

/** Stable per-day key: re-scheduling a task to another day re-arms its reminder. */
export const reminderKey = task => `${task.id}@${task.date}T${task.startTime}`;

/** 'in 8 minutes' / 'now' — the phrasing the reminder itself uses. */
export function reminderLead(task, now = Date.now()) {
  const start = taskStartMs(task);
  if (start == null) return 'now';
  const mins = Math.round((start - now) / 60000);
  if (mins <= 0) return 'now';
  return `in ${mins} minute${mins === 1 ? '' : 's'}`;
}

// ── Tidying up carried work ────────────────────────────────────────────────

/** Open activities that have rolled over at least `threshold` times. */
export function staleTasks(tasks, threshold = DEFAULT_SETTINGS.staleAfterCarries) {
  const min = Math.max(1, Number(threshold) || 1);
  return (tasks || []).filter(t => !t.done && !t.archived && (t.carryCount || 0) >= min);
}

/**
 * Bulk-resolve stale work. `mode` is 'defer' (comes back next week, carry count
 * reset so it gets a clean run) or 'shelve' (out of every view, restorable from
 * the Review tab). Returns the new list plus what it took, so the caller can
 * offer an undo.
 */
export function tidyStale(tasks, ids, mode, today = todayStr()) {
  const set = new Set(ids || []);
  const before = (tasks || []).filter(t => set.has(t.id)).map(t => ({ ...t }));
  const next = (tasks || []).map(t => {
    if (!set.has(t.id)) return t;
    return mode === 'shelve'
      ? { ...t, archived: true, shelvedAt: Date.now() }
      : { ...t, date: addDays(today, 7), carryCount: 0, originalDate: addDays(today, 7) };
  });
  return { tasks: next, before };
}

/** Put back exactly what a tidy-up (or any bulk action) changed. */
export function restoreTasks(tasks, before) {
  const byId = new Map((before || []).map(t => [t.id, t]));
  return (tasks || []).map(t => byId.get(t.id) || t);
}

/** Shelved activities, most recently shelved first — the Review tab's list. */
export function shelvedTasks(tasks) {
  return (tasks || [])
    .filter(t => t.archived && !t.done)
    .sort((a, b) => (b.shelvedAt || 0) - (a.shelvedAt || 0));
}

// ── Deleting ───────────────────────────────────────────────────────────────

/** Deleted work is restorable for this long before it is dropped for good. */
export const TRASH_DAYS = 30;

/**
 * Move activities to the trash instead of dropping them.
 *
 * A planner is somewhere you tidy up fast, often on a phone, often one-handed
 * — so a delete has to be survivable. Deleted work keeps its whole record and
 * sits in the trash for TRASH_DAYS, where it can be restored or dropped for
 * good; the Expense Calculator's deleted months work the same way.
 */
export function trashTasks(tasks, ids, trash = [], now = Date.now()) {
  const set = new Set(ids || []);
  const removed = (tasks || []).filter(t => set.has(t.id));
  return {
    tasks: (tasks || []).filter(t => !set.has(t.id)),
    trash: [...removed.map(t => ({ ...t, deletedAt: now })), ...(trash || [])],
    removed,
  };
}

/** Put trashed activities back on the list, newest edit wins over the stub. */
export function restoreFromTrash(tasks, trash, ids) {
  const set = new Set(ids || []);
  const coming = (trash || []).filter(t => set.has(t.id)).map(t => {
    const { deletedAt, ...task } = t;
    return task;
  });
  const have = new Set((tasks || []).map(t => t.id));
  return {
    tasks: [...(tasks || []), ...coming.filter(t => !have.has(t.id))],
    trash: (trash || []).filter(t => !set.has(t.id)),
    restored: coming,
  };
}

/** Drop anything deleted longer ago than TRASH_DAYS. */
export function pruneTrash(trash, now = Date.now(), days = TRASH_DAYS) {
  const cutoff = now - days * 86400000;
  return (trash || []).filter(t => (t.deletedAt || 0) >= cutoff);
}

/** Days left before a trashed activity is dropped for good. */
export function trashDaysLeft(task, now = Date.now(), days = TRASH_DAYS) {
  return Math.max(0, Math.ceil(((task.deletedAt || 0) + days * 86400000 - now) / 86400000));
}

// ── The cloud document ─────────────────────────────────────────────────────
// Kept here (Firebase-free) so the page can fingerprint exactly what it would
// write without importing Firestore; myDayCloud.js does the actual I/O.

/** Completed work older than this is pruned on save to keep the doc small. */
export const KEEP_DONE_DAYS = 120;

/** Firestore rejects `undefined`; every value we store is JSON-safe. */
const clean = value => JSON.parse(JSON.stringify(value ?? null));

/** Drop long-finished tasks so the single doc stays small. */
export function pruneTasks(tasks, today = todayStr()) {
  const cutoff = addDays(today, -KEEP_DONE_DAYS);
  return (tasks || []).filter(t => {
    if (!t.done && !t.archived) return true;
    return (t.doneDate || t.date || today) >= cutoff;
  });
}

/** The planner fields exactly as they are written to myDayData/{uid}. */
export function myDayPayload({ tasks, routines, habits, days, settings, categories, trash }) {
  return {
    tasks: clean(pruneTasks(tasks)) || [],
    // Deleted work is restorable for 30 days, then it goes for good.
    trash: clean(pruneTrash(trash)) || [],
    routines: clean(routines) || [],
    habits: clean(habits) || [],
    days: clean(days) || {},
    settings: clean(settings) || {},
    categories: clean(categories) || [],
  };
}

/** JSON with object keys sorted, so a map Firestore returns in another key order still matches. */
function stableStringify(v) {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
  if (v && typeof v === 'object') {
    return `{${Object.keys(v).sort().map(k => `${JSON.stringify(k)}:${stableStringify(v[k])}`).join(',')}}`;
  }
  return JSON.stringify(v);
}

/**
 * Fingerprint of a planner payload. Lets the page tell "the cloud already has
 * this" (its own write coming back, or a load with nothing to change) from a
 * real edit — see the save effect in MyDay.js.
 */
export const payloadSig = payload => stableStringify(payload);

// ── Planning the next day ──────────────────────────────────────────────────

/** Hour rows the planner draws, from the working day's start to its end. */
export function planHours(settings = DEFAULT_SETTINGS) {
  const from = Math.max(0, Math.min(23, Number(settings.startHour ?? 7)));
  const to = Math.max(from + 1, Math.min(24, Number(settings.endHour ?? 21)));
  return Array.from({ length: to - from }, (_, i) => from + i);
}

/** 'HH:00' for an hour number — what the slot picker writes onto a task. */
export const hourSlot = h => `${String(h).padStart(2, '0')}:00`;

/**
 * Split a day into what is on the clock and what is not.
 *
 * Time-blocked work is grouped by the hour it starts in, so the timeline can
 * render an hour row and everything inside it, in order.
 */
export function planDay(tasks, date) {
  const onDay = (tasks || []).filter(t => !t.archived && !t.done && t.date === date);
  const timed = onDay.filter(t => t.startTime);
  const anytime = sortTasks(onDay.filter(t => !t.startTime));
  const byHour = new Map();
  timed.forEach(t => {
    const hour = Number(String(t.startTime).split(':')[0]);
    if (!byHour.has(hour)) byHour.set(hour, []);
    byHour.get(hour).push(t);
  });
  byHour.forEach(list => list.sort((a, b) => (a.startTime < b.startTime ? -1 : 1)));
  return { timed, anytime, byHour, total: onDay.length };
}

/**
 * What the owner can pull into the day they are planning.
 *
 * Three buckets, in the order Sunsama's planning ritual asks for them:
 * what is chasing you (still open today or earlier), what you already put off
 * until later, and what you shelved. Anything already on the target day is
 * excluded — it is in the day, not waiting to go in.
 */
export function planBacklog(tasks, date, today = todayStr()) {
  const open = (tasks || []).filter(t => !t.done && t.date !== date);
  return {
    carrying: sortTasks(open.filter(t => !t.archived && diffDays(t.date, today) <= 0)),
    later: sortTasks(open.filter(t => !t.archived && diffDays(t.date, today) > 0)),
    shelved: shelvedTasks(tasks),
  };
}

/**
 * How full the planned day is against the time actually left to work.
 *
 * Capacity is the working window itself — from the day's start to the hour the
 * owner says they want to be finished — rather than an abstract number, which
 * is what makes "you have planned nine hours into a six-hour day" land.
 */
export function planCapacity(dayTasks, settings = DEFAULT_SETTINGS) {
  const start = Number(settings.startHour ?? 7);
  const finish = Number(settings.finishByHour ?? 19);
  const capacityMin = Math.max(30, (finish - start) * 60);
  const plannedMin = (dayTasks || [])
    .filter(t => !t.done && !t.archived)
    .reduce((sum, t) => sum + (Number(t.estimateMin) || 0), 0);
  const pct = Math.round((plannedMin / capacityMin) * 100);
  const overMin = Math.max(0, plannedMin - capacityMin);
  return {
    plannedMin, capacityMin, pct, overMin,
    untimedCount: (dayTasks || []).filter(t => !t.done && !t.archived && !t.estimateMin).length,
    state: pct > 110 ? 'over' : pct > 85 ? 'tight' : 'ok',
  };
}

// ── Manual ordering ────────────────────────────────────────────────────────

/**
 * Swipe distances, shared with the CSS. Matched to Daily Operations so a swipe
 * feels the same everywhere in the app.
 */
export const SWIPE = { max: 100, threshold: 68, lock: 8, animMs: 180 };

/**
 * Move one activity up or down within its own group.
 *
 * "Order your work" is the last step of a planning ritual and the only one the
 * sort could not express: `order` existed on every task and sortTasks honoured
 * it, but nothing ever wrote it. This renumbers the group from 0 so the values
 * stay small and stable however many times the owner shuffles them.
 *
 * `siblingIds` is the group as it appears on screen, already sorted — the
 * caller knows the grouping, this does not.
 */
export function reorderTask(tasks, id, delta, siblingIds) {
  const order = [...(siblingIds || [])];
  const from = order.indexOf(id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= order.length) return tasks;
  order.splice(to, 0, ...order.splice(from, 1));
  const rank = new Map(order.map((taskId, i) => [taskId, i]));
  return (tasks || []).map(t => (rank.has(t.id) ? { ...t, order: rank.get(t.id) } : t));
}

/** Can this activity move that way, given where it sits in its group? */
export function canMove(id, delta, siblingIds) {
  const i = (siblingIds || []).indexOf(id);
  return i >= 0 && i + delta >= 0 && i + delta < siblingIds.length;
}

// ── Stats ──────────────────────────────────────────────────────────────────

/** Everything that belonged to `date`: done there, still open there, or rolled off it. */
export function tasksOnDay(tasks, date) {
  return (tasks || []).filter(t => {
    if (t.archived) return false;
    return t.done ? (t.doneDate || t.date) === date : t.date === date;
  });
}

/**
 * Completion counts for a single day.
 *
 * `days[date].rolled` is the ledger the planner stamps when it carries work
 * forward. Without it a day the owner abandoned would look perfect in
 * hindsight, because everything they skipped has already moved to today.
 */
export function dayProgress(tasks, date, days = {}) {
  const onDay = tasksOnDay(tasks, date);
  const done = onDay.filter(t => t.done).length;
  const rolled = Math.max(0, Number(days?.[date]?.rolled) || 0);
  const total = onDay.length + rolled;
  return { total, done, rolled, pct: total ? Math.round((done / total) * 100) : 0 };
}

/**
 * Rolling history + streaks. A day "counts" when everything planned on it was
 * finished; the streak walks backwards from today (today only breaks it once
 * it is over, so an in-progress day never zeroes yesterday's run).
 */
export function computeStats(tasks, today = todayStr(), span = 14, days = {}) {
  const history = [];
  for (let i = span - 1; i >= 0; i--) {
    const date = addDays(today, -i);
    const p = dayProgress(tasks, date, days);
    history.push({ date, done: p.done, planned: p.total });
  }

  let streak = 0;
  for (let i = 0; i < 400; i++) {
    const date = addDays(today, -i);
    const p = dayProgress(tasks, date, days);
    if (p.total === 0) { if (i === 0) continue; break; }
    if (p.done === p.total) streak++;
    else if (i === 0) continue; // today is still in play
    else break;
  }

  const completed = (tasks || []).filter(t => t.done);
  const last7 = history.slice(-7);
  const doneLast7 = last7.reduce((s, d) => s + d.done, 0);
  const plannedLast7 = last7.reduce((s, d) => s + d.planned, 0);

  return {
    history,
    streak,
    doneLast7,
    plannedLast7,
    rateLast7: plannedLast7 ? Math.round((doneLast7 / plannedLast7) * 100) : 0,
    totalDone: completed.length,
    focusMin: Math.round((tasks || []).reduce((s, t) => s + (t.focusSec || 0), 0) / 60),
    carriedOpen: (tasks || []).filter(t => !t.done && !t.archived && (t.carryCount || 0) > 0).length,
  };
}

/** Per-category completion, biggest first — the Insights breakdown. */
export function categoryBreakdown(tasks, cats = DEFAULT_CATEGORIES) {
  const map = new Map();
  (tasks || []).filter(t => !t.archived).forEach(t => {
    const key = t.category || 'general';
    const row = map.get(key) || { id: key, meta: categoryMeta(key, cats), total: 0, done: 0 };
    row.total++;
    if (t.done) row.done++;
    map.set(key, row);
  });
  return [...map.values()]
    .map(r => ({ ...r, pct: r.total ? Math.round((r.done / r.total) * 100) : 0 }))
    .sort((a, b) => b.total - a.total);
}

// ── Habits ─────────────────────────────────────────────────────────────────

export function habitDue(habit, date) {
  const days = habit.days;
  if (!days || days.length === 0 || days.length === 7) return true;
  return days.includes(dayOfWeek(date));
}

/** Current + best run of due days that were ticked. */
export function habitStreak(habit, today = todayStr()) {
  const log = habit.log || {};
  let current = 0;
  for (let i = 0; i < 365; i++) {
    const date = addDays(today, -i);
    if (!habitDue(habit, date)) continue;
    if (log[date]) current++;
    else if (i === 0) continue; // today isn't a miss until it's over
    else break;
  }
  let best = 0, run = 0;
  const dates = Object.keys(log).filter(d => log[d]).sort();
  if (dates.length) {
    let cursor = dates[0];
    const last = dates[dates.length - 1];
    while (diffDays(cursor, last) <= 0) {
      if (habitDue(habit, cursor)) {
        if (log[cursor]) { run++; best = Math.max(best, run); } else run = 0;
      }
      cursor = addDays(cursor, 1);
    }
  }
  return { current, best: Math.max(best, current) };
}

// ── Export ─────────────────────────────────────────────────────────────────

/**
 * Columns for the CSV export. The building and the download itself go through
 * the shared Tickets csv helper, which quotes formula-leading text (a title
 * starting with `=` would otherwise open as a live formula in Excel) and
 * writes the BOM Excel needs to read UTF-8.
 */
export function csvColumns(cats = DEFAULT_CATEGORIES) {
  return [
    { key: 'date',       label: 'Date' },
    { key: 'title',      label: 'Title' },
    { key: 'status',     label: 'Status',         get: t => (t.done ? 'Done' : 'Open') },
    { key: 'priority',   label: 'Priority',       get: t => priorityMeta(t.priority).short },
    { key: 'category',   label: 'Category',       get: t => (t.category ? categoryMeta(t.category, cats).name : '') },
    { key: 'tags',       label: 'Tags',           get: t => (t.tags || []).join(' ') },
    { key: 'estimate',   label: 'Estimate (min)', get: t => t.estimateMin ?? '' },
    { key: 'startTime',  label: 'Time',           get: t => t.startTime || '' },
    { key: 'carryCount', label: 'Carried',        get: t => t.carryCount || 0 },
    { key: 'focus',      label: 'Focus (min)',    get: t => Math.round((t.focusSec || 0) / 60) },
    { key: 'doneAt',     label: 'Completed at',   get: t => (t.doneAt ? new Date(t.doneAt).toLocaleString() : '') },
    { key: 'notes',      label: 'Notes',          get: t => (t.notes || '').replace(/\n/g, ' ') },
  ];
}
